import { create } from 'zustand';
import { SensorData, HeatmapData } from '../types';

interface MockDataStore {
  // State
  sensorData: SensorData[];
  heatmapData: HeatmapData[];
  realTimeEnabled: boolean;
  updateInterval: number;

  // Data operations
  generateMockData: () => void;
  updateSensorData: (sensorId: string, data: Partial<SensorData>) => void;
  toggleRealTime: () => void;
  setUpdateInterval: (interval: number) => void;

  // Utilities
  getSensorDataById: (sensorId: string) => SensorData | null;
  getHeatmapDataByPosition: (position: [number, number, number], radius: number) => HeatmapData[];
}

const SENSOR_TYPES = ['temperature', 'humidity', 'pressure', 'motion', 'light', 'air-quality'];

const generateValue = (type: string): number => {
  switch (type) {
    case 'temperature':
      return 20 + Math.random() * 15; // 20-35°C
    case 'humidity':
      return 40 + Math.random() * 40; // 40-80%
    case 'pressure':
      return 1000 + Math.random() * 50; // 1000-1050 hPa
    case 'motion':
      return Math.random() > 0.8 ? 1 : 0; // Motion detected
    case 'light':
      return Math.random() * 1000; // 0-1000 lux
    case 'air-quality':
      return Math.random() * 500; // 0-500 AQI
    default:
      return Math.random() * 100;
  }
};

const getUnit = (type: string): string => {
  switch (type) {
    case 'temperature':
      return '°C';
    case 'humidity':
      return '%';
    case 'pressure':
      return 'hPa';
    case 'motion':
      return 'detected';
    case 'light':
      return 'lux';
    case 'air-quality':
      return 'AQI';
    default:
      return 'units';
  }
};

const getMinMaxValues = (type: string): { min: number; max: number } => {
  switch (type) {
    case 'temperature':
      return { min: 15, max: 40 };
    case 'humidity':
      return { min: 0, max: 100 };
    case 'air-quality':
      return { min: 0, max: 500 };
    default:
      return { min: 0, max: 100 };
  }
};

const generateHistory = (type: string, count = 20): Array<{ value: number; timestamp: Date }> => {
  const history = [];
  const now = new Date();

  for (let i = count - 1; i >= 0; i--) {
    history.push({
      value: generateValue(type),
      timestamp: new Date(now.getTime() - i * 5 * 60 * 1000), // 5 minutes intervals
    });
  }

  return history;
};

const getSensorTypeFromUnit = (unit: string): string => {
  switch (unit) {
    case '°C':
      return 'temperature';
    case '%':
      return 'humidity';
    case 'hPa':
      return 'pressure';
    case 'detected':
      return 'motion';
    case 'lux':
      return 'light';
    case 'AQI':
      return 'air-quality';
    default:
      return 'temperature';
  }
};

export const useMockDataStore = create<MockDataStore>((set, get) => ({
  // Initial state
  sensorData: [],
  heatmapData: [],
  realTimeEnabled: false,
  updateInterval: 2000,

  // Data operations
  generateMockData: () => {
    const mockSensorData: SensorData[] = [];
    const mockHeatmapData: HeatmapData[] = [];

    // Generate sensor data
    for (let i = 0; i < 25; i++) {
      const type = SENSOR_TYPES[Math.floor(Math.random() * SENSOR_TYPES.length)];
      const value = generateValue(type);
      const position: [number, number, number] = [
        Math.random() * 50 - 25, // -25 to 25
        1 + Math.random() * 3, // 1 to 4 (height)
        Math.random() * 50 - 25, // -25 to 25
      ];

      const sensorData: SensorData = {
        value,
        unit: getUnit(type),
        timestamp: new Date(),
        history: generateHistory(type),
      };

      mockSensorData.push(sensorData);

      // Create corresponding heatmap data for visualizable types
      const visualizableTypes = ['temperature', 'humidity', 'air-quality'];
      if (visualizableTypes.includes(type)) {
        const { min, max } = getMinMaxValues(type);
        const heatmapData: HeatmapData = {
          id: `heatmap-${i}`,
          position,
          value,
          minValue: min,
          maxValue: max,
          unit: getUnit(type),
          timestamp: new Date(),
        };

        mockHeatmapData.push(heatmapData);
      }
    }

    set({
      sensorData: mockSensorData,
      heatmapData: mockHeatmapData,
    });
  },

  updateSensorData: (sensorId, data) => {
    set((state) => ({
      sensorData: state.sensorData.map((sensor, index) =>
        index.toString() === sensorId ? { ...sensor, ...data } : sensor,
      ),
    }));
  },

  toggleRealTime: () => {
    set((state) => ({
      realTimeEnabled: !state.realTimeEnabled,
    }));
  },

  setUpdateInterval: (interval) => {
    set({ updateInterval: Math.max(500, interval) });
  },

  // Utilities
  getSensorDataById: (sensorId) => {
    const { sensorData } = get();
    const index = parseInt(sensorId, 10);
    return sensorData[index] ?? null;
  },

  getHeatmapDataByPosition: (position, radius) => {
    const { heatmapData } = get();
    return heatmapData.filter((data) => {
      const dx = data.position[0] - position[0];
      const dy = data.position[1] - position[1];
      const dz = data.position[2] - position[2];
      const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
      return distance <= radius;
    });
  },
}));

// Export utility functions for external use
export { generateValue, getUnit, getSensorTypeFromUnit };
